/* THE EDITORS' SAVE LINE — W72 step 3 (Proposal 3's head: `Saved · 00:43 · Copy · Done`). Pure, so the component asks
 * and the tests drive every case. 🔴 W95 (his words, 2026-09-29): BOTH the workout builder and this exercise editor save
 * ONLY ON SAVE — the autosave (`autosaveDue`, `AUTOSAVE_MS`) is gone, and `saveStatus` below is the one line both heads
 * print. (The file keeps its name: renaming it moves every import for no reader's benefit.) */

/** 1…10 — `coach_exercises.difficulty` (smallint, NULL = unrated). */
export const DIFFICULTIES: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

function hhmm(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** When it was saved, as the head says it: the clock time today, `22/9 03:20` on another day — so a coach can tell
 *  "saved" from "saved last week" (Fable's ask). The day is written as W71's `21/9`, no leading zeros. */
export function savedWhen(at: Date, now: Date): string {
  const sameDay = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  return sameDay ? hhmm(at) : `${at.getDate()}/${at.getMonth() + 1} ${hhmm(at)}`;
}

/** The head's one line about saving. `warn` only where the coach has something to do. */
export function saveStatus(s: {
  saving: boolean; error: string | null; dirty: boolean; problems: readonly string[]; savedAt: Date | null; exists: boolean;
  now?: Date;
}): { text: string; tone: "quiet" | "warn" } {
  if (s.saving) return { text: "Saving…", tone: "quiet" };
  if (s.error !== null) return { text: "Not saved — see below", tone: "warn" };
  if (s.dirty && s.problems.length > 0) return { text: `Not saved — ${s.problems[0]}`, tone: "warn" };
  // W95: a change waits for Save, so it is UNSAVED — "Saving…" there would claim a write nobody asked for.
  if (s.dirty) return { text: "Unsaved changes", tone: "quiet" };
  if (s.savedAt !== null) return { text: `Saved · ${savedWhen(s.savedAt, s.now ?? new Date())}`, tone: "quiet" };
  return { text: s.exists ? "Saved" : "", tone: "quiet" };
}

/** A copy's name: `Step-Up (copy)`, then `(copy 2)`, `(copy 3)` … — the first name `taken` does not hold — and a copy of
 *  a copy does not stack the word. `taken` is the coach's names LOWER-CASED, the unique index's own comparison
 *  (`coach_exercises_unique_name_idx … (coach_id, lower(name))`).
 *
 *  🔴 W83-4 (Fable's ruling, 2026-09-27): the rule was `X (copy)` alone, so a second copy of X collided with that index
 *  and failed with "Could not copy" on the web while the phone named its copies `X copy`, `X copy 2`. Fixed ONCE, on both:
 *  the rule is Swift's `CoachLibraryFacts.copyName`, and this is held EQUAL to it on the corpus the Swift emits
 *  (`coachLibraryCorpus.test.ts`). */
export function copyName(name: string, taken: ReadonlySet<string>): string {
  const base = name.trim().replace(/\s*\(copy(?: \d+)?\)$/i, "");
  const first = `${base} (copy)`;
  if (!taken.has(first.toLowerCase())) return first;
  let n = 2;
  while (taken.has(`${base} (copy ${String(n)})`.toLowerCase())) n += 1;
  return `${base} (copy ${String(n)})`;
}
